import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
// eslint-disable-next-line no-restricted-imports
import { useHistory, withRouter } from 'react-router-dom'
import { usePrevious } from 'react-use'
import { bindActionCreators } from 'redux'

import Alert from '@/components/Alert'
import { getCurrentOrganizationId } from '@/components/App/selectors'
import ErrorConfirmation from '@/components/ErrorConfirmation'
import Page from '@/components/Page'
import UserAttributeForm, { getCustomSchemaUrn } from '@/pages/OrganizationUserAttributeCreatePage/UserAttributeForm'
import { fetchAttributes } from '@/pages/OrganizationUserAttributesPage/actions'
import { FETCH_ATTRIBUTES } from '@/pages/OrganizationUserAttributesPage/actionTypes'
import attributeShape from '@/pages/OrganizationUserAttributesPage/attributeShape'
import {
  makeGetAttributeById,
  makeGetDisplayNamesBySchema,
  makeGetSCIMAttributeNamesBySchema
} from '@/pages/OrganizationUserAttributesPage/selectors'
import { resetRequest } from '@/store/actions'
import { isRequestError, isRequestPending, isRequestSuccessful } from '@/store/selectors'

import { editAttribute } from './actions'
import { EDIT_ATTRIBUTE } from './actionTypes'
import messages from './messages'

function OrganizationUserAttributeEditPage(props: any) {
  const intl = useIntl()
  const history = useHistory()

  // componentDidMount
  useEffect(() => {
    props.actions.resetRequest([EDIT_ATTRIBUTE, FETCH_ATTRIBUTES])
    props.actions.fetchAttributes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // componentDidUpdate
  const prevProps = usePrevious(props)
  useEffect(() => {
    if (!prevProps) return

    if (!prevProps.isEditSuccessful && props.isEditSuccessful) {
      navigateToAttributeList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, prevProps])

  const navigateToAttributeList = () => {
    history.push('/organization/users/attributes')
  }

  const onCancel = () => {
    navigateToAttributeList()
  }

  const onSubmit = ({ description, displayName, scimAttributeName, targetable, type }: any) => {
    props.actions.editAttribute({
      ...props.canonical,
      description,
      displayName,
      scimAttributeName,
      targetable,
      type
    })
  }

  const isDisplayNameAvailable = (value: any) => {
    return (
      !value ||
      props.canonical.displayName.toLowerCase() === value.toLowerCase() ||
      !props.displayNames.some((name: any) => name.toLowerCase() === value.toLowerCase())
    )
  }

  const isSCIMAttributeNameAvailable = (value: any) => {
    return (
      !value ||
      props.canonical.scimAttributeName.toLowerCase() === value.toLowerCase() ||
      !props.scimAttributeNames.some((name: any) => name.toLowerCase() === value.toLowerCase())
    )
  }

  const { canonical, hasError, isLoading } = props
  let disabledAttributes: any = []
  if (canonical) {
    if (canonical.custom) {
      disabledAttributes = disabledAttributes.concat(['scimAttributeName', 'type'])
    } else {
      disabledAttributes = disabledAttributes.concat(['displayName', 'scimAttributeName', 'type'])
    }

    if (canonical.scimParentAttributeName) {
      disabledAttributes.push('targetable')
    }
  }

  return (
    <Page
      isLoading={isLoading || (!canonical && !hasError)}
      loadingMessage={<FormattedMessage {...messages.loading} />}
    >
      <Page.Header
        backNavTitle={<FormattedMessage {...messages.back} />}
        goBack={onCancel}
        title={<FormattedMessage {...messages.title} />}
      />

      {hasError ? (
        <ErrorConfirmation
          error={intl.formatMessage(messages.errorTitle)}
          subMessage={intl.formatMessage(messages.errorMessage)}
        />
      ) : (
        !!canonical && (
          <UserAttributeForm
            disabled={disabledAttributes}
            onCancel={onCancel}
            onSubmit={onSubmit}
            submitTitle={messages.submitTitle}
            testDisplayName={isDisplayNameAvailable}
            testSCIMAttributeName={isSCIMAttributeNameAvailable}
            types={canonical.custom ? ['binary', 'boolean', 'datetime', 'decimal', 'integer', 'string'] : undefined}
            values={{
              description: canonical.description,
              displayName: canonical.displayName,
              scimAttributeName: canonical.scimAttributeName,
              scimParentAttributeName: canonical.scimParentAttributeName,
              targetable: `${canonical.targetable}`,
              type: canonical.type
            }}
          >
            {!canonical.custom && (
              <Alert type="warning">
                <FormattedMessage {...messages.defaultAttributeWarningMessage} />
              </Alert>
            )}
          </UserAttributeForm>
        )
      )}
    </Page>
  )
}

OrganizationUserAttributeEditPage.displayName = 'OrganizationUserAttributeEditPage'

OrganizationUserAttributeEditPage.propTypes = {
  actions: PropTypes.shape({
    editAttribute: PropTypes.func.isRequired,
    fetchAttributes: PropTypes.func.isRequired,
    resetRequest: PropTypes.func.isRequired
  }).isRequired,
  canonical: attributeShape,
  displayNames: PropTypes.arrayOf(PropTypes.string),
  hasError: PropTypes.bool,
  isEditSuccessful: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  organizationId: PropTypes.string.isRequired,
  scimAttributeNames: PropTypes.arrayOf(PropTypes.string)
}

OrganizationUserAttributeEditPage.defaultProps = {
  canonical: null,
  displayNames: [],
  hasError: false,
  scimAttributeNames: []
}

const DisconnectedOrganizationUserAttributeEditPage = OrganizationUserAttributeEditPage
export { /** @deprecated */ DisconnectedOrganizationUserAttributeEditPage as OrganizationUserAttributeEditPage }

const makeMapStateToProps = () => {
  const getAttributeById = makeGetAttributeById()
  const getDisplayNamesBySchema = makeGetDisplayNamesBySchema()
  const getSCIMAttributeNamesBySchema = makeGetSCIMAttributeNamesBySchema()

  return (state: any, ownProps: any) => {
    const organizationId = getCurrentOrganizationId(state)
    const schemaUrn = getCustomSchemaUrn(organizationId)

    return {
      organizationId,
      canonical: getAttributeById(state, ownProps.match.params.attributeId),
      displayNames: getDisplayNamesBySchema(state, schemaUrn),
      hasError: isRequestError(state, [EDIT_ATTRIBUTE, FETCH_ATTRIBUTES]),
      isEditSuccessful: isRequestSuccessful(state, EDIT_ATTRIBUTE),
      isLoading: isRequestPending(state, [EDIT_ATTRIBUTE, FETCH_ATTRIBUTES]),
      scimAttributeNames: getSCIMAttributeNamesBySchema(state, schemaUrn)
    }
  }
}

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators({ editAttribute, fetchAttributes, resetRequest }, dispatch)
})

export default withRouter(
  connect(makeMapStateToProps, mapDispatchToProps)(DisconnectedOrganizationUserAttributeEditPage)
)
